import { makeAutoObservable } from "mobx"
import React from "react"

import { t } from "@lingui/macro"

import { AdminService } from "src/api"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { createLoadingKeys } from "src/lib/loading"
import { reportSuccess, reportUnhandledApiError } from "src/lib/report"
import { ILegalEntity } from "src/views/client-management/legal-entity/store"

export interface ICopyLegalEntitySettingsPayload {
    integrationActive: boolean
}

export class CopyLegalEntitySettingsStore {
    static Context = React.createContext<CopyLegalEntitySettingsStore | null>(
        null,
    )
    static LoadingKeys = createLoadingKeys("submit")

    legalEntities: ILegalEntity[] = []
    private payload: ICopyLegalEntitySettingsPayload | null = null
    private selectedForCopy = new Set<number>()

    constructor() {
        makeAutoObservable(this)
    }

    init(
        legalEntities: ILegalEntity[],
        payload: ICopyLegalEntitySettingsPayload,
    ) {
        this.legalEntities = legalEntities
        this.payload = payload
        this.selectedForCopy.clear()
    }

    get allSelected() {
        if (this.legalEntities.length === 0) {
            return false
        }

        return this.legalEntities.every((legalEntity) =>
            this.selectedForCopy.has(legalEntity.legalEntityId),
        )
    }

    setAllSelected(checked: boolean) {
        if (checked) {
            this.legalEntities.forEach((legalEntity) =>
                this.selectedForCopy.add(legalEntity.legalEntityId),
            )
        } else {
            this.selectedForCopy.clear()
        }
    }

    toggleCopy(legalEntity: ILegalEntity) {
        const id = legalEntity.legalEntityId
        if (this.selectedForCopy.has(id)) {
            this.selectedForCopy.delete(id)
        } else {
            this.selectedForCopy.add(id)
        }
    }

    willCopy(legalEntity: ILegalEntity) {
        return this.selectedForCopy.has(legalEntity.legalEntityId)
    }

    hasSelected() {
        return this.selectedForCopy.size > 0
    }

    selected() {
        return Array.from(this.selectedForCopy)
    }

    @loads(() => CopyLegalEntitySettingsStore.LoadingKeys.submit)
    async submit() {
        if (this.selectedForCopy.size === 0 || this.payload == null) {
            return
        }

        const ids = this.selected()

        const payload = this.payload
        const integrationActive = payload.integrationActive ?? false

        try {
            await Promise.all(
                ids.map((id) =>
                    AdminService.patchV1AdminLegalEntity({
                        legalEntityId: id,
                        request: {
                            integration_active: integrationActive,
                        },
                    }),
                ),
            )

            ids.forEach((id) => {
                const legalEntity = this.legalEntities.find(
                    (entity) => entity.legalEntityId === id,
                )
                Channel.send({
                    name: "repository/updated",
                    payload: {
                        repository: "legal-entities",
                        action: "update",
                        item: {
                            id,
                            name: legalEntity?.legalName ?? "",
                        },
                    },
                })
            })

            reportSuccess(
                t`copy-legal-entity-settings-modal.copy-success-message`,
            )
        } catch (e) {
            reportUnhandledApiError(e)
            throw e
        }
    }
}
